/**
 * Utility functions for loading and managing custom fonts
 */

// Cache of loaded fonts to avoid loading the same font multiple times
const loadedFontsCache = new Set();

/**
 * Load a font from a URL and register it with the document
 * @param {string} fontName - Name to use for the font
 * @param {string} fontUrl - URL to the font file
 * @returns {Promise<boolean>} - Promise that resolves to true if font loaded successfully
 */
/**
 * Add timestamp to URL to prevent caching
 * @param {string} url - URL to add timestamp to
 * @returns {string} - URL with timestamp parameter
 */
const addTimestampToUrl = (url) => {
  if (!url) return url;
  const timestamp = new Date().getTime();
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}t=${timestamp}`;
};

export const loadFontFromUrl = async (fontName, fontUrl) => {
  try {
    // Check if the font is already loaded
    if (loadedFontsCache.has(fontName)) {
      
      return true;
    }
    
    // Add timestamp to prevent browser caching
    const urlWithTimestamp = addTimestampToUrl(fontUrl);
    
    
    const fontFace = new FontFace(fontName, `url(${urlWithTimestamp})`);
    const loadedFont = await fontFace.load();
    document.fonts.add(loadedFont);
    
    // Add to cache
    loadedFontsCache.add(fontName);
    
    
    return true;
  } catch (error) {
    console.error(`Error loading font ${fontName}:`, error);
    return false;
  }
};

/**
 * Parse a CSV file containing font information
 * @param {string} csvUrl - URL to the CSV file
 * @returns {Promise<Array>} - Array of font objects with name, url and displayName properties
 */
export const parseFontCsv = async (csvUrl) => {
  try {
    
    const response = await fetch(csvUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch CSV from ${csvUrl}`);
    }
    
    const text = await response.text();
    const lines = text.split('\n');
    
    // Check if we have any data
    if (lines.length <= 1) {
      console.warn('CSV file is empty or only contains headers');
      return [];
    }
    
    // Parse header to determine format
    const header = lines[0].toLowerCase().trim();
    const isObjectUrlFormat = header.includes('object') && header.includes('url');
    
    
    // Parse the CSV data
    const fonts = [];
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;
      
      const parts = line.split(',');
      if (parts.length < 2) continue;
      
      const url = parts[1].trim();
      
      try {
        // Generate a font name from the URL
        const urlObj = new URL(url);
        const pathParts = urlObj.pathname.split('/');
        const fileName = decodeURIComponent(pathParts[pathParts.length - 1]);
        
        // Extract full name without file extension
        let displayName = fileName.replace(/\.[^/.]+$/, '');
        
        // For Chinese fonts, keep the full name for display
        // But also extract the font number for internal naming
        const chineseFontMatch = displayName.match(/(字魂\d+号)/i);
        
        // Create a safe ASCII font name for the FontFace API
        // Extract font number from Chinese font names or use line index
        const fontNumber = displayName.match(/字魂(\d+)号/)?.[1] || i;
        
        // Ensure uniqueness by adding a unique identifier
        // Use a combination of font number and timestamp to guarantee uniqueness
        const uniqueId = `${Date.now().toString(36)}-${Math.random().toString(36).substr(2, 5)}`;
        const fontName = `Font${fontNumber}_${uniqueId}`;
        
        // Add to fonts array
        fonts.push({
          name: fontName,
          url: url,
          displayName: displayName,
          loaded: false
        });
      } catch (error) {
        console.error(`Error parsing font URL: ${url}`, error);
      }
    }
    
    
    return fonts;
  } catch (error) {
    console.error('Error parsing fonts from CSV:', error);
    return [];
  }
};

/**
 * Load a specific font by its name
 * @param {Object} fontInfo - Font information object with name, url and displayName
 * @returns {Promise<boolean>} - Promise that resolves to true if font loaded successfully
 */
export const loadSpecificFont = async (fontInfo) => {
  if (!fontInfo || !fontInfo.name || !fontInfo.url) {
    console.error('Invalid font information provided');
    return false;
  }
  
  return await loadFontFromUrl(fontInfo.name, fontInfo.url);
};

/**
 * Preload the first few fonts from a list
 * @param {Array} fonts - Array of font objects
 * @param {number} count - Number of fonts to preload
 * @returns {Promise<Array>} - Array of preloaded font names
 */
export const preloadInitialFonts = async (fonts, count = 5) => {
  if (!fonts || !Array.isArray(fonts) || fonts.length === 0) {
    return [];
  }
  
  const fontsToLoad = fonts.slice(0, Math.min(count, fonts.length));
  
  
  const loadPromises = fontsToLoad.map(async (font) => {
    const success = await loadFontFromUrl(font.name, font.url);
    if (success) {
      font.loaded = true;
      return font.name;
    }
    return null;
  });
  
  const results = await Promise.all(loadPromises);
  const loadedFonts = results.filter(Boolean);
  
  
  return loadedFonts;
};
